/**
 * Athlete Self Nutrition Page — Phase 2.1B (read-only)
 *
 * Bound to: GET /api/nutrition-analytics/athlete/{my_athlete_id}
 *
 * Single scrollable page where the athlete sees their own:
 *   • Profile (allergies / intolerances / dietary preferences)
 *   • Anthropometry history
 *   • Body composition history
 *   • Active target + current meal plan
 *
 * No edit buttons — the athlete cannot mutate any nutrition data.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Apple, Loader2, Utensils, AlertTriangle } from 'lucide-react';
import {
  Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import BodyCompositionSummaryCard from '../../components/nutrition/BodyCompositionSummaryCard';
import BodyCompositionTimeline from '../../components/nutrition/BodyCompositionTimeline';
import TargetSummaryCard from '../../components/nutrition/TargetSummaryCard';
import MealPlanSummaryCard from '../../components/nutrition/MealPlanSummaryCard';
import MealPlanViewer from '../../components/nutrition/MealPlanViewer';

const fmt = (v, dp = 1, suffix = '') =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}${suffix}`;
const fmtInt = (v, suffix = '') =>
  v === null || v === undefined ? '—' : `${Math.round(v)}${suffix}`;

const StatCard = ({ label, value, hint, testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
    <div className="text-2xl font-bold text-cyan-300">{value}</div>
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </Card>
);

const AthleteNutritionHomePage = () => {
  const { user, api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [bodyCompRows, setBodyCompRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.athlete_id) {
      setError(language === 'IT' ? 'Profilo atleta non collegato' : 'Athlete profile not linked');
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const [resA, resT, resBC] = await Promise.all([
          api.get(`/nutrition-analytics/athlete/${user.athlete_id}`),
          api.get(`/nutrition/today-plan/${user.athlete_id}`),
          api.get(`/nutrition/body-composition/${user.athlete_id}`).catch(() => ({ data: [] })),
        ]);
        setData(resA.data);
        setToday(resT.data);
        setBodyCompRows(resBC.data || []);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, user, language]);

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        {language === 'IT' ? 'Caricamento…' : 'Loading…'}
      </div>
    );
  }
  if (error || !data || data.error) {
    return (
      <div className="p-12 text-center text-zinc-400" data-testid="athlete-self-nutrition-error">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
        <p>{error || (language === 'IT' ? 'Nessun dato nutrizionale ancora.' : 'No nutrition data yet.')}</p>
      </div>
    );
  }

  const profile = data.profile;
  const target = data.active_target;
  const latest = data.anthropometry?.latest;
  const anthroTimeline = (data.anthropometry?.timeline || []).map((r) => ({
    date: r.date,
    weight: r.weight_kg,
    bf: r.body_fat_pct,
  }));
  const currentVersion = data.meal_plan?.current_version;

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="athlete-self-nutrition">
      <header>
        <div className="flex items-center gap-3 mb-1">
          <Apple className="w-6 h-6 text-emerald-400" />
          <h1 className="text-3xl font-bold text-zinc-100">
            {language === 'IT' ? 'La mia nutrizione' : 'My nutrition'}
          </h1>
        </div>
        <p className="text-sm text-zinc-400">
          {language === 'IT'
            ? 'Visualizzazione sola lettura del tuo profilo nutrizionale.'
            : 'Read-only view of your nutrition profile.'}
        </p>
      </header>

      {/* Today — Day Type Resolver banner (Phase 2.2) */}
      {today && (
        <Card className="bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border-cyan-500/40 p-5" data-testid="today-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-cyan-300 mb-1">
                {language === 'IT' ? 'Oggi' : 'Today'} · {today.resolution?.date}
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-cyan-500/20 text-cyan-200 border-cyan-500/40 text-base px-3 py-1">
                  {today.resolution?.day_type}
                </Badge>
                {today.fallback_to_generic && (
                  <Badge variant="outline" className="border-amber-500/40 text-amber-300">
                    {language === 'IT' ? 'Piano generico' : 'Generic plan fallback'}
                  </Badge>
                )}
                {today.empty_state && (
                  <span className="text-amber-300 text-sm">
                    {language === 'IT' ? 'Nessun piano disponibile per oggi' : 'No plan available for today'}
                  </span>
                )}
              </div>
              {today.resolution?.rules_applied?.length > 0 && (
                <p className="text-xs text-zinc-400 mt-2">
                  {today.resolution.rules_applied[0]}
                </p>
              )}
            </div>
            {today.plan && (
              <div className="text-right">
                <div className="text-xs text-zinc-500">{language === 'IT' ? 'Piano assegnato' : 'Assigned plan'}</div>
                <div className="text-zinc-100 font-medium">v{today.plan.version} · {today.plan.label}</div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Profile preferences */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="profile-card">
        <h2 className="text-lg font-semibold text-zinc-100 mb-3">
          {language === 'IT' ? 'Preferenze e restrizioni' : 'Preferences & restrictions'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase text-zinc-500 mb-1">{language === 'IT' ? 'Allergie' : 'Allergies'}</div>
            <div className="flex flex-wrap gap-1.5">
              {(profile?.allergies || []).length === 0 ? (
                <span className="text-zinc-500 text-sm">—</span>
              ) : profile.allergies.map((a, i) => (
                <Badge key={i} className="bg-red-500/20 text-red-300 border-red-500/40">{a}</Badge>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-zinc-500 mb-1">{language === 'IT' ? 'Intolleranze' : 'Intolerances'}</div>
            <div className="flex flex-wrap gap-1.5">
              {(profile?.intolerances || []).length === 0 ? (
                <span className="text-zinc-500 text-sm">—</span>
              ) : profile.intolerances.map((a, i) => (
                <Badge key={i} className="bg-amber-500/20 text-amber-300 border-amber-500/40">{a}</Badge>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-zinc-500 mb-1">{language === 'IT' ? 'Preferenze alimentari' : 'Dietary preferences'}</div>
            <div className="flex flex-wrap gap-1.5">
              {(profile?.dietary_preferences || []).length === 0 ? (
                <span className="text-zinc-500 text-sm">—</span>
              ) : profile.dietary_preferences.map((a, i) => (
                <Badge key={i} variant="outline" className="border-zinc-700 text-zinc-300">{a}</Badge>
              ))}
            </div>
          </div>
          {profile?.nutrition_notes && (
            <div className="md:col-span-2">
              <div className="text-xs uppercase text-zinc-500 mb-1">{language === 'IT' ? 'Note' : 'Notes'}</div>
              <p className="text-sm text-zinc-300">{profile.nutrition_notes}</p>
            </div>
          )}
        </div>
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="my-weight" label={language === 'IT' ? 'Peso attuale' : 'Latest weight'} value={fmt(latest?.weight_kg, 1, ' kg')} />
        <StatCard testid="my-bf" label="BF %" value={fmt(latest?.body_fat_pct, 1, ' %')} />
        <StatCard testid="my-bmr" label="BMR" value={fmtInt(latest?.bmr_kcal, ' kcal')} hint={latest?.bmr_formula} />
        <StatCard testid="my-tdee" label="TDEE" value={fmtInt(latest?.tdee_kcal, ' kcal')} hint={latest?.activity_level} />
      </div>

      {/* Trend */}
      {anthroTimeline.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="my-anthro-chart">
          <h2 className="text-lg font-semibold text-zinc-100 mb-3">
            {language === 'IT' ? 'Andamento misure' : 'Measurements trend'}
          </h2>
          <div className="h-64 min-h-[16rem]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={anthroTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" stroke="#06b6d4" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="weight" name={language === 'IT' ? 'Peso (kg)' : 'Weight (kg)'} stroke="#06b6d4" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="bf" name="BF %" stroke="#f59e0b" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Body composition — Phase 2.2B Session 2 (read-only) */}
      <div data-testid="my-body-comp-section" className="space-y-4">
        <BodyCompositionSummaryCard rows={bodyCompRows} />
        <BodyCompositionTimeline rows={bodyCompRows} />
      </div>

      {/* Active target — Phase 2.2B Session 3 */}
      <div data-testid="my-target-card">
        <TargetSummaryCard
          target={target}
          weightKg={data.anthropometry?.latest?.weight_kg}
        />
      </div>

      {/* Today's meal plan — Phase 2.2B Session 4 (day-type resolver) */}
      <div data-testid="my-meal-plan-card" className="space-y-4">
        <MealPlanSummaryCard
          version={today?.plan}
          plan={null}
          dayType={today?.resolution?.day_type}
          fallbackToGeneric={today?.fallback_to_generic}
        />
        {today?.plan && <MealPlanViewer version={today.plan} />}
        {today?.empty_state && !today?.plan && (
          <Card className="bg-zinc-900 border-zinc-800 p-5 text-center text-sm text-zinc-500" data-testid="my-meal-plan-empty">
            {language === 'IT' ? 'Nessun piano disponibile per oggi.' : 'No plan available for today.'}
          </Card>
        )}
      </div>
    </div>
  );
};

export default AthleteNutritionHomePage;
